import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import AccessibleProgressBar from '@/components/common/AccessibleProgressBar';
import userService from '@/shared/services/userService';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { TEAM_EMAIL } from '@/shared/utils/contact';
import { withTeamEmailLink } from '@/components/common/TeamEmailLink';

const TOUR_STEPS = [
    {
        title: 'Welcome to Peer Support Hub!',
        body: "You're now part of MSU Denver's coordinated peer support system. Let's take a quick look at what you can do here.",
        icon: '\uD83C\uDF93',
    },
    {
        title: 'Three Programs, One Hub',
        body: 'The Hub coordinates Learning Assistants (LA), Supplemental Instruction (SI), and Teaching Assistants (TA) - all in one place.',
        icon: '\uD83D\uDD17',
    },
    {
        title: 'Find Positions',
        body: 'See the course sections that are hiring this term. Filter by day, time, or format to find the ones that fit your schedule.',
        icon: '\uD83D\uDCDA',
        link: '/positions',
        linkLabel: 'Browse Positions',
        feature: 'studentApplication',
    },
    {
        title: 'Apply for Positions',
        body: 'Answer the application once, then apply to each position you could accept. Your applications and any offers are listed under Applications.',
        icon: '\uD83D\uDCDD',
        link: '/applications',
        linkLabel: 'Applications',
        feature: 'studentApplication',
    },
    {
        title: 'Track Your Assignments',
        body: 'Once assigned, view your course and schedule, and keep an activity journal through the semester.',
        icon: '\uD83D\uDCCB',
        link: '/assignments',
        linkLabel: 'View Assignments',
    },
    {
        title: 'Complete Training',
        body: 'Each program has specific training requirements. Track your progress and complete modules right from the dashboard.',
        icon: '\uD83C\uDFAF',
        link: '/training',
        linkLabel: 'View Training',
        // Shown only while the training flag is on (#391); getTourSteps
        // drops it otherwise, so the tour never points at a page that is off.
        feature: 'training',
    },
    {
        title: 'Your Profile',
        body: 'Keep your profile up to date with your major, contact info, and qualifications. A complete profile helps coordinators match you to courses.',
        icon: '\uD83D\uDC64',
        link: '/profile',
        linkLabel: 'Edit Profile',
    },
    {
        title: "You're All Set!",
        body: 'That covers the basics. You can always access these features from the navigation menu. Good luck!',
        icon: '\u2705',
    },
];

const COORDINATOR_TOUR_STEPS = [
    {
        title: 'Welcome, Coordinator!',
        body: "You manage one of MSU Denver's peer support programs. This tour covers the tools available to you.",
        icon: '\uD83C\uDF93',
    },
    {
        title: 'Your Dashboard',
        body: 'Your dashboard shows pending actions, recent activity, and a summary of your program. Everything you need is one click away.',
        icon: '\uD83D\uDCCA',
    },
    {
        title: 'Review Queue',
        body: 'Applications waiting for your review appear in your queue. Filter by course, term, or applicant and open any application to read the full details.',
        icon: '\uD83D\uDCCB',
        feature: 'coordinatorReview',
    },
    {
        title: 'Recording Decisions',
        body: 'For each application you can approve, deny, waitlist, or issue a contingent award. Contingent awards become final if a higher-ranked program passes on the applicant.',
        icon: '\uD83D\uDCDD',
        feature: 'coordinatorReview',
    },
    {
        title: 'Configuring Review Windows',
        body: 'Set the open and close dates for your program review window each term. Applications become visible to you once your window opens.',
        icon: '\uD83D\uDDD3\uFE0F',
        feature: 'coordinatorReview',
    },
    {
        title: "You're All Set!",
        body: `That covers your core tools. Contact the Peer Support Hub team at ${TEAM_EMAIL} if you have questions.`,
        icon: '\u2705',
    },
];

const ADMIN_TOUR_STEPS = [
    {
        title: 'Welcome, Administrator!',
        body: 'You have full access to the Peer Support Hub. This tour covers user management, program configuration, and term setup.',
        icon: '\uD83C\uDF93',
    },
    {
        title: 'Your Dashboard',
        body: 'The admin dashboard gives you a system-wide view: active applications, decision status across all three programs, and any items needing attention.',
        icon: '\uD83D\uDCCA',
    },
    {
        title: 'User Management',
        body: 'View, edit, and assign roles to any user in the system. You can promote a student to coordinator, deactivate accounts, and audit role assignments.',
        icon: '\uD83D\uDC65',
    },
    {
        title: 'Program Configuration',
        body: 'Each program (LA, SI, TA) has its own eligibility rules and training requirements. Configure them here so the system enforces the right criteria automatically.',
        icon: '\u2699\uFE0F',
    },
    {
        title: 'Term Setup',
        body: 'Create and configure academic terms: set application open and close dates, define each program review window, and activate the term when ready.',
        icon: '\uD83D\uDCC5',
    },
    {
        title: "You're All Set!",
        body: 'You have full control of the system. Start with term setup at the beginning of each cycle and keep program configs current as requirements change.',
        icon: '\u2705',
    },
];

const FACULTY_TOUR_STEPS = [
    {
        title: 'Welcome, Faculty!',
        body: 'You can request peer support for your courses and follow each request through the Peer Support Hub. This tour covers the tools available to you.',
        icon: '\uD83C\uDF93',
    },
    {
        title: 'Your Dashboard',
        body: "Your dashboard shows each program's application window this term and the courses that already have peer support.",
        icon: '\uD83D\uDCCA',
    },
    {
        title: 'Requesting Support',
        body: 'Choose New support request, find the course section you teach, then select the programs you want and rank them. A program not accepting applications this term is marked Coming soon.',
        icon: '\uD83D\uDCDD',
        link: '/applications/start',
        linkLabel: 'New support request',
    },
    {
        title: 'Following Your Requests',
        body: "Support requests lists every request you have started or submitted, with each program's decision as it arrives.",
        icon: '\uD83D\uDCCB',
        link: '/applications',
        linkLabel: 'Support requests',
    },
    {
        title: "You're All Set!",
        body: 'Use the navigation menu to request support or check on a request at any time.',
        icon: '\u2705',
    },
];

function stepsForRoles(userRoles) {
    if (userRoles.includes('admin')) {
        return ADMIN_TOUR_STEPS;
    } else if (
        userRoles.some(
            (role) =>
                role === 'si_coordinator' ||
                role === 'la_coordinator' ||
                role === 'ta_coordinator'
        )
    ) {
        return COORDINATOR_TOUR_STEPS;
    } else if (userRoles.includes('faculty')) {
        return FACULTY_TOUR_STEPS;
    } else {
        return TOUR_STEPS;
    }
}

/**
 * The tour for these roles, without any step whose feature flag is off.
 *
 * Every caller that counts steps (the tour, the resume banner, the dashboard)
 * goes through here with the same flags, so they agree on the length. A flag
 * missing from `features` counts as off, matching useFeature.
 *
 * @param {string[]} userRoles
 * @param {Object<string, boolean>} [features] e.g. { training: true }
 */
function getTourSteps(userRoles, features = {}) {
    return stepsForRoles(userRoles).filter(
        (step) => !step.feature || features[step.feature] === true
    );
}

/**
 * The flags getTourSteps reads, for every caller that counts steps, so the
 * tour, the resume banner and the dashboard cannot disagree on the length.
 */
export function useTourFeatures() {
    return {
        training: useFeature('training'),
        studentApplication: useFeature('studentApplication'),
        coordinatorReview: useFeature('coordinatorReview'),
    };
}

OnboardingTour.propTypes = {
    show: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    initialStep: PropTypes.number,
    userRoles: PropTypes.arrayOf(PropTypes.string),
};

export default function OnboardingTour({
    show,
    onComplete,
    initialStep = 0,
    userRoles = [],
}) {
    const navigate = useNavigate();
    const tourSteps = getTourSteps(userRoles, useTourFeatures());
    const safeInitialStep =
        initialStep >= 0 && initialStep < tourSteps.length ? initialStep : 0;

    const [step, setStep] = useState(safeInitialStep);

    const currentStep = tourSteps[step];
    const isLast = step === tourSteps.length - 1;
    const progress = ((step + 1) / tourSteps.length) * 100;

    const handleComplete = async () => {
        try {
            await userService.markTourComplete();
        } catch (_err) {
            // tour completion is best-effort; navigate out regardless
        }
        onComplete();
    };

    const handleSkip = async () => {
        await handleComplete();
    };

    const handleNext = async () => {
        const nextStep = step + 1;
        if (isLast) {
            await handleComplete();
        } else {
            setStep((s) => s + 1);
            userService.saveTourStep(nextStep).catch(() => {});
        }
    };

    const handleLinkClick = async (link) => {
        const nextStep = step + 1;
        try {
            await userService.saveTourStep(nextStep);
        } catch (_err) {
            // step save is best-effort; navigate out regardless
        }
        onComplete();
        navigate(link);
    };

    if (!show) return null;

    return (
        <Modal
            show={show}
            centered
            backdrop="static"
            keyboard={false}
            size="md"
        >
            <Modal.Body className="p-4 text-center">
                <div className="mb-3" aria-hidden="true">
                    <span className="display-1">{currentStep.icon}</span>
                </div>
                <h4 className="text-primary mb-3">{currentStep.title}</h4>
                <p className="text-muted mb-4">
                    {withTeamEmailLink(currentStep.body)}
                </p>

                {currentStep.link && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded-pill mb-3"
                        onClick={() => handleLinkClick(currentStep.link)}
                    >
                        {currentStep.linkLabel}
                    </Button>
                )}

                <AccessibleProgressBar
                    now={progress}
                    className="mb-3"
                    variant="primary"
                    label={`Step ${step + 1} of ${tourSteps.length}`}
                />

                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted"
                        onClick={handleSkip}
                    >
                        Skip Tour
                    </Button>
                    <div className="d-flex gap-2">
                        {step > 0 && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="rounded-pill"
                                onClick={() => setStep((s) => s - 1)}
                            >
                                Previous
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="sm"
                            className="rounded-pill"
                            onClick={handleNext}
                        >
                            {isLast ? 'Get Started' : 'Next'}
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export const TOUR_STEP_COUNT = TOUR_STEPS.length;
export { getTourSteps };
